// packages/shared-components/src/components/Popconfirm/Popconfirm.tsx

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import { getSubAppPortalContainer } from "@eweadn-qiankun/shared-utils";
import { Iconfont } from "../Iconfont";
import { PopconfirmProps, PopconfirmPlacement } from "./types";
import "./Popconfirm.css";

// 样式常量
const OFFSETS = {
  default: 14,
  top: 22,
  left: 20,
  padding: 4,
  borderRadius: 8,
  arrowSize: 10,
  arrowInitialOffset: 16,
  safetyMargin: 4,
} as const;

const BOUNDARIES = {
  header: 64,
  sidebar: 200,
} as const;

// 计算可用空间
function calculateAvailableSpace(
  triggerRect: DOMRect,
  viewportWidth: number,
  viewportHeight: number
) {
  return {
    above: triggerRect.top,
    below: viewportHeight - triggerRect.bottom,
    left: triggerRect.left,
    right: viewportWidth - triggerRect.right,
  };
}

// 自动调整方位
function getAdjustedPlacement(
  placement: PopconfirmPlacement,
  space: ReturnType<typeof calculateAvailableSpace>,
  popoverRect: DOMRect
): PopconfirmPlacement {
  if (placement.startsWith("top")) {
    if (
      space.above < popoverRect.height + OFFSETS.top &&
      space.below > space.above
    ) {
      return placement.replace("top", "bottom") as PopconfirmPlacement;
    }
  } else if (placement.startsWith("bottom")) {
    if (
      space.below < popoverRect.height + OFFSETS.default &&
      space.above > space.below
    ) {
      return placement.replace("bottom", "top") as PopconfirmPlacement;
    }
  } else if (placement.startsWith("left")) {
    if (
      space.left < popoverRect.width + OFFSETS.left &&
      space.right > space.left
    ) {
      return placement.replace("left", "right") as PopconfirmPlacement;
    }
  } else if (placement.startsWith("right")) {
    if (
      space.right < popoverRect.width + OFFSETS.default &&
      space.left > space.right
    ) {
      return placement.replace("right", "left") as PopconfirmPlacement;
    }
  }
  return placement;
}

// 计算基础位置
function calculateBasePosition(
  placement: PopconfirmPlacement,
  triggerRect: DOMRect,
  popoverRect: DOMRect
): { top: number; left: number } {
  const { top: topOffset, left: leftOffset, default: offset } = OFFSETS;

  if (placement.startsWith("top")) {
    const top = triggerRect.top - popoverRect.height - topOffset;
    if (placement === "topLeft") return { top, left: triggerRect.left };
    if (placement === "topRight")
      return { top, left: triggerRect.right - popoverRect.width };
    return {
      top,
      left: triggerRect.left + (triggerRect.width - popoverRect.width) / 2,
    };
  }

  if (placement.startsWith("bottom")) {
    const top = triggerRect.bottom + offset;
    if (placement === "bottomLeft") return { top, left: triggerRect.left };
    if (placement === "bottomRight")
      return { top, left: triggerRect.right - popoverRect.width };
    return {
      top,
      left: triggerRect.left + (triggerRect.width - popoverRect.width) / 2,
    };
  }

  if (placement.startsWith("left")) {
    const left = triggerRect.left - popoverRect.width - leftOffset;
    if (placement === "leftTop") return { top: triggerRect.top, left };
    if (placement === "leftBottom")
      return { top: triggerRect.bottom - popoverRect.height, left };
    return {
      top: triggerRect.top + (triggerRect.height - popoverRect.height) / 2,
      left,
    };
  }

  // right
  const left = triggerRect.right + leftOffset;
  if (placement === "rightTop") return { top: triggerRect.top, left };
  if (placement === "rightBottom")
    return { top: triggerRect.bottom - popoverRect.height, left };
  return {
    top: triggerRect.top + (triggerRect.height - popoverRect.height) / 2,
    left,
  };
}

// 边界调整
function adjustForBoundaries(
  position: { top: number; left: number },
  popoverRect: DOMRect,
  viewportWidth: number,
  viewportHeight: number
): {
  position: { top: number; left: number };
  arrowOffset: { x: number; y: number };
} {
  const { padding } = OFFSETS;
  const originalLeft = position.left;
  const originalTop = position.top;
  let { left, top } = position;
  let arrowX = 0;
  let arrowY = 0;

  // 水平边界
  if (left < padding) {
    left = padding;
    arrowX = originalLeft - left;
  } else if (left + popoverRect.width > viewportWidth - padding) {
    left = viewportWidth - popoverRect.width - padding;
    arrowX = originalLeft - left;
  }

  // 垂直边界
  if (top < padding) {
    top = padding;
    arrowY = originalTop - top;
  } else if (top + popoverRect.height > viewportHeight - padding) {
    top = viewportHeight - popoverRect.height - padding;
    arrowY = originalTop - top;
  }

  return { position: { top, left }, arrowOffset: { x: arrowX, y: arrowY } };
}

// 限制箭头偏移范围
function clampArrowOffset(
  placement: PopconfirmPlacement,
  arrowOffset: { x: number; y: number },
  popoverRect: DOMRect
): { x: number; y: number } {
  const { borderRadius, arrowSize, arrowInitialOffset, safetyMargin } = OFFSETS;
  let { x: arrowX, y: arrowY } = arrowOffset;

  if (placement.startsWith("top") || placement.startsWith("bottom")) {
    const safeRangeX =
      popoverRect.width - borderRadius * 2 - arrowSize - safetyMargin * 2;

    if (placement === "top" || placement === "bottom") {
      arrowX = Math.max(-safeRangeX / 2, Math.min(safeRangeX / 2, arrowX));
    } else if (placement === "topLeft" || placement === "bottomLeft") {
      const maxOffset =
        popoverRect.width -
        arrowInitialOffset -
        borderRadius -
        arrowSize -
        safetyMargin;
      arrowX = Math.max(0, Math.min(maxOffset, arrowX));
    } else {
      const maxOffset =
        popoverRect.width -
        arrowInitialOffset -
        borderRadius -
        arrowSize -
        safetyMargin;
      arrowX = Math.max(-maxOffset, Math.min(0, arrowX));
    }
  } else if (placement.startsWith("left") || placement.startsWith("right")) {
    const safeRangeY =
      popoverRect.height - borderRadius * 2 - arrowSize - safetyMargin * 2;

    if (placement === "left" || placement === "right") {
      arrowY = Math.max(-safeRangeY / 2, Math.min(safeRangeY / 2, arrowY));
    } else if (placement === "leftTop" || placement === "rightTop") {
      const maxOffset =
        popoverRect.height -
        arrowInitialOffset -
        borderRadius -
        arrowSize -
        safetyMargin;
      arrowY = Math.max(0, Math.min(maxOffset, arrowY));
    } else {
      const maxOffset =
        popoverRect.height -
        arrowInitialOffset -
        borderRadius -
        arrowSize -
        safetyMargin;
      arrowY = Math.max(-maxOffset, Math.min(0, arrowY));
    }
  }

  return { x: arrowX, y: arrowY };
}

// 防抖函数
function debounce<T extends (...args: any[]) => any>(fn: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const debounced = function (this: any, ...args: Parameters<T>) {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      timeoutId = null;
      fn.apply(this, args);
    }, delay);
  } as T & { cancel: () => void };

  debounced.cancel = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounced;
}

export const Popconfirm: React.FC<PopconfirmProps> = ({
  title,
  description,
  children,
  okText,
  cancelText,
  onConfirm,
  onCancel,
  showCancel = true,
  placement = "top",
  icon,
  disabled = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowOffset, setArrowOffset] = useState({ x: 0, y: 0 });
  const [actualPlacement, setActualPlacement] = useState(placement);
  const [animated, setAnimated] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const debouncedUpdateRef = useRef<ReturnType<
    typeof debounce<() => void>
  > | null>(null);

  // 计算弹窗位置
  const calculatePosition = () => {
    if (!triggerRef.current || !popoverRef.current) return;

    try {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();

      // 容错：检查 DOM 元素是否有效（宽高不为 0）
      if (popoverRect.width === 0 || popoverRect.height === 0) {
        console.warn(
          "Popconfirm: Popover element has zero dimensions, skipping position calculation"
        );
        return;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // 1. 计算可用空间并调整方位
      const space = calculateAvailableSpace(
        triggerRect,
        viewportWidth,
        viewportHeight
      );
      const finalPlacement = getAdjustedPlacement(
        placement,
        space,
        popoverRect
      );
      setActualPlacement(finalPlacement);

      // 2. 计算基础位置
      const basePosition = calculateBasePosition(
        finalPlacement,
        triggerRect,
        popoverRect
      );

      // 3. 边界调整并计算箭头偏移
      const { position: adjustedPosition, arrowOffset } = adjustForBoundaries(
        basePosition,
        popoverRect,
        viewportWidth,
        viewportHeight
      );

      // 4. 限制箭头偏移范围
      const clampedArrowOffset = clampArrowOffset(
        finalPlacement,
        arrowOffset,
        popoverRect
      );

      setPosition(adjustedPosition);
      setArrowOffset(clampedArrowOffset);
    } catch (error) {
      console.error("Popconfirm: Error calculating position", error);
    }
  };

  // 检测触发元素是否在可见区域内
  const checkTriggerVisibility = () => {
    if (!triggerRef.current) return true;

    const rect = triggerRef.current.getBoundingClientRect();
    const rightBoundary = window.innerWidth;
    const bottomBoundary = window.innerHeight;

    // 完全滚出可见区域时关闭
    const isCompletelyOutside =
      rect.bottom < BOUNDARIES.header ||
      rect.top > bottomBoundary ||
      rect.right < BOUNDARIES.sidebar ||
      rect.left > rightBoundary;

    if (isCompletelyOutside) {
      setVisible(false);
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (!visible) {
      // 隐藏时取消防抖任务
      if (debouncedUpdateRef.current) {
        debouncedUpdateRef.current.cancel();
      }
      return;
    }

    setAnimated(false);
    calculatePosition();

    // 动画结束后启用 transition
    const animationTimer = setTimeout(() => {
      setAnimated(true);
    }, 200);

    // 创建稳定的防抖函数引用
    const handlePositionUpdate = () => {
      if (checkTriggerVisibility()) {
        calculatePosition();
      }
    };

    const debouncedUpdate = debounce(handlePositionUpdate, 16); // ~60fps
    debouncedUpdateRef.current = debouncedUpdate;

    // 事件监听选项：scroll 使用 passive 提升性能
    const scrollOptions = { capture: true, passive: true };

    window.addEventListener("resize", debouncedUpdate);
    window.addEventListener("scroll", debouncedUpdate, scrollOptions);

    return () => {
      clearTimeout(animationTimer);

      // 取消待执行的防抖任务
      if (debouncedUpdateRef.current) {
        debouncedUpdateRef.current.cancel();
      }

      window.removeEventListener("resize", debouncedUpdate);
      window.removeEventListener("scroll", debouncedUpdate, scrollOptions);
    };
  }, [visible, placement]);

  // 点击外部关闭
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        triggerRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        !triggerRef.current.contains(e.target as Node)
      ) {
        onCancel?.();
        setVisible(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [visible, onCancel]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      if (debouncedUpdateRef.current) {
        debouncedUpdateRef.current.cancel();
      }
    };
  }, []);

  const handleTriggerClick = () => {
    if (disabled) return;
    setVisible(!visible);
  };

  const handleConfirm = async () => {
    if (!onConfirm) {
      setVisible(false);
      return;
    }

    try {
      setLoading(true);
      const result = onConfirm();
      if (result instanceof Promise) {
        await result;
      }
      setVisible(false);
    } catch (error) {
      console.error("Popconfirm confirm error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
    setVisible(false);
  };

  const getArrowClass = () => {
    return `popconfirm-arrow popconfirm-arrow-${actualPlacement}`;
  };

  const getArrowStyle = (): React.CSSProperties & Record<string, string> => {
    const style: React.CSSProperties & Record<string, string> = {};
    if (arrowOffset.x !== 0) {
      style["--arrow-offset-x"] = `${arrowOffset.x}px`;
    }
    if (arrowOffset.y !== 0) {
      style["--arrow-offset-y"] = `${arrowOffset.y}px`;
    }
    return style;
  };

  return (
    <>
      <div
        ref={triggerRef}
        className="popconfirm-trigger"
        onClick={handleTriggerClick}
      >
        {children}
      </div>

      {visible &&
        createPortal(
          <div
            ref={popoverRef}
            className={`popconfirm-container ${animated ? "popconfirm-container--animated" : ""} ${className}`}
            style={{
              position: "fixed",
              top: `${position.top}px`,
              left: `${position.left}px`,
              zIndex: 1100,
            }}
          >
            <Iconfont
              icon={
                actualPlacement.startsWith("top")
                  ? "downarrow"
                  : actualPlacement.startsWith("bottom")
                    ? "uparrow"
                    : actualPlacement.startsWith("left")
                      ? "rightarrow"
                      : "leftarrow"
              }
              className={getArrowClass()}
              style={getArrowStyle()}
            />
            <div className="popconfirm-content">
              <div className="popconfirm-message">
                {icon !== undefined && (
                  <span className="popconfirm-icon">{icon}</span>
                )}
                {icon === undefined && (
                  <span className="popconfirm-icon">
                    <Iconfont icon="information" />
                  </span>
                )}
                <div className="popconfirm-message-text">
                  <div className="popconfirm-title">{title}</div>
                  {description && (
                    <div className="popconfirm-description">{description}</div>
                  )}
                </div>
              </div>
              <div className="popconfirm-buttons">
                {showCancel && (
                  <button
                    className="popconfirm-btn popconfirm-btn-cancel"
                    onClick={handleCancel}
                    disabled={loading}
                  >
                    {cancelText || t("common.cancel")}
                  </button>
                )}
                <button
                  className="popconfirm-btn popconfirm-btn-ok popconfirm-btn-danger"
                  onClick={handleConfirm}
                  disabled={loading}
                >
                  {loading && (
                    <span className="popconfirm-loading-icon">
                      <Iconfont icon="loading" />
                    </span>
                  )}
                  {okText || t("common.confirm")}
                </button>
              </div>
            </div>
          </div>,
          getSubAppPortalContainer()
        )}
    </>
  );
};
